import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import ToggleSwitch from 'react-switch';

import { CONTENT_VERIFICATION_STATUS_OPTIONS } from 'lib/enums/ContentVerificationStatus';
import {
  DATA_STREAM_GET_BY_ID,
  DATA_STREAM_SYNC_JOB,
  getURL,
  getURLWithSearchParams,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  GLOBAL_VENDORS_BY_IDS,
} from 'lib/networking/endpoints';
import { DATA_STREAM_ENTITY } from 'lib/enums/DataStreamEnum';
import { grape } from 'lib/css/colors';
import { httpGet } from 'lib/networking/http';
import { ProductActiveStatusOptions } from 'lib/enums/ProductActiveStatus';
import { removeEmpty } from 'lib/utils/objectUtils';
import SearchableScrollableMultiSelect from 'components/shared/SearchableScrollableMultiSelect';
import ThirdPartyDropdown from 'components/shared/ThirdPartyDropdown';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import usePut from 'lib/hooks/usePut';

function CreateUpdateDataStreamSyncJob({
  onHide,
  dataStreamId,
  updateDataStreamSyncJobs,
  dataStreamSyncJob,
  dataStream,
}) {
  const [newDataStreamSyncJob, setNewDataStreamSyncJob] = useState({
    destinationLocationUrl: dataStreamSyncJob ? dataStreamSyncJob.destination_location_url : '',
    cronExpression: dataStreamSyncJob ? dataStreamSyncJob.cron_expression : '',
    contentVerificationStatus: dataStreamSyncJob?.content_verification_status_filter || null,
    productStatus: dataStreamSyncJob?.product_status_filter || [],
    processDeltaFile: dataStreamSyncJob?.process_delta || false,
    syncTempSkusOnly: dataStreamSyncJob?.sync_temp_skus_only_filter || false,
    selectedVendorIds: dataStream?.multiple_entities?.distributor_ids || [],
    fileName: dataStreamSyncJob?.file_name || '',
    lookbackAttribute: dataStreamSyncJob?.lookback_attribute || null,
  });

  const [initOptions, setInitOptions] = useState([]);

  // Third party state for multi-vendor streams
  const currentThirdParty = dataStream?.multiple_entities?.third_party || null;
  const [considerThirdParty, setConsiderThirdParty] = useState(!!currentThirdParty);
  const [selectedThirdParty, setSelectedThirdParty] = useState(
    currentThirdParty ? { label: currentThirdParty, value: currentThirdParty } : null,
  );

  // Fetch data stream details to get attribute_map_with_order
  const streamId = dataStream?.id || dataStreamId;
  const { data: dataStreamDetails, loading: loadingDataStream } = useGet(
    getURL(DATA_STREAM_GET_BY_ID, { data_stream_id: streamId }),
    {},
    !streamId,
  );

  // Build attribute options from data stream's attribute map
  const attributeOptions = useMemo(() => {
    const attributeMap = dataStreamDetails?.attribute_map_with_order;
    if (!attributeMap) {
      return [];
    }
    return Object.entries(attributeMap).map(([columnName, attr]) => ({
      value: attr.attribute_name,
      label: `${columnName} (${attr.attribute_name})`,
    }));
  }, [dataStreamDetails?.attribute_map_with_order]);

  // initial load
  useEffect(() => {
    const url = getURLWithSearchParams(
      GLOBAL_VENDORS_BY_IDS,
      removeEmpty({
        ids: newDataStreamSyncJob.selectedVendorIds?.length
          ? newDataStreamSyncJob.selectedVendorIds.join(',')
          : null,
      }),
    );
    httpGet(url).then(res => {
      if (res.status === 200) {
        const vendors = res.data?.data;
        if (vendors?.length) {
          setInitOptions(vendors.map(vendor => ({ label: vendor.name, value: vendor.id })));
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { postData: createNewDataStreamSyncJob, loading } = usePost(
    DATA_STREAM_SYNC_JOB,
    res => {
      Swal.fire({
        icon: 'success',
        text: 'Successfully created',
        title: 'Success',
        position: 'center',
      }).then(() => {
        updateDataStreamSyncJobs(res.data.data);
      });
    },
    () => {
      Swal.fire({
        icon: 'error',
        text: 'An error occurred while creating',
        title: 'Error',
        position: 'center',
      });
    },
  );

  const { putData: updateStreamSyncJob } = usePut(
    DATA_STREAM_SYNC_JOB,
    res => {
      Swal.fire({
        icon: 'success',
        text: 'Successfully updated',
        title: 'Success',
        position: 'center',
      }).then(() => {
        updateDataStreamSyncJobs(res.data.data, true);
      });
    },
    () => {
      Swal.fire({
        icon: 'error',
        text: 'An error occurred while updating',
        title: 'Error',
        position: 'center',
      });
    },
  );

  return (
    <>
      <Modal centered show={true} onHide={onHide} animation={false}>
        <Modal.Header>
          <h4>{dataStreamId ? 'Edit' : 'Add'} Sync Job For Stream</h4>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Location URL</Form.Label>
            <Form.Control
              onChange={e =>
                setNewDataStreamSyncJob({
                  ...newDataStreamSyncJob,
                  destinationLocationUrl: e.target.value,
                })
              }
              value={newDataStreamSyncJob.destinationLocationUrl}
            />
          </Form.Group>
          {dataStream?.entity_type === DATA_STREAM_ENTITY.MULTI_VENDOR && (
            <Form.Group>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Enter only the name. Example: if the file needed is sample.csv then only enter
                    `sample`
                  </Tooltip>
                }
              >
                <Form.Label>Exported File Name (Optional)</Form.Label>
              </OverlayTrigger>
              <Form.Control
                onChange={e =>
                  setNewDataStreamSyncJob({
                    ...newDataStreamSyncJob,
                    fileName: e.target.value,
                  })
                }
                value={newDataStreamSyncJob.fileName}
              />
            </Form.Group>
          )}
          <Form.Group>
            <Form.Label>Cron Expression</Form.Label>
            <Form.Control
              onChange={e =>
                setNewDataStreamSyncJob({
                  ...newDataStreamSyncJob,
                  cronExpression: e.target.value,
                })
              }
              value={newDataStreamSyncJob.cronExpression}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Filter Content Verified Products</Form.Label>
            <Select
              isClearable
              options={CONTENT_VERIFICATION_STATUS_OPTIONS}
              value={CONTENT_VERIFICATION_STATUS_OPTIONS.filter(
                op => op.value === newDataStreamSyncJob.contentVerificationStatus,
              )}
              onChange={op =>
                setNewDataStreamSyncJob({
                  ...newDataStreamSyncJob,
                  contentVerificationStatus: op?.value,
                })
              }
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Filter Active Products</Form.Label>
            <Select
              isMulti
              isClearable
              options={ProductActiveStatusOptions}
              value={ProductActiveStatusOptions.filter(op =>
                newDataStreamSyncJob.productStatus?.includes(op.value),
              )}
              onChange={selectedOptions =>
                setNewDataStreamSyncJob({
                  ...newDataStreamSyncJob,
                  productStatus: selectedOptions?.map(op => op.value) || [],
                })
              }
            />
          </Form.Group>
          {/* Third party toggle and vendor selection for multi-vendor streams */}
          {dataStream?.entity_type === DATA_STREAM_ENTITY.MULTI_VENDOR && (
            <>
              <Row className="mb-3">
                <Col>
                  <Form.Label className="mt-1">Consider Third Party</Form.Label>
                </Col>
                <Col className="d-flex align-items-center justify-content-end">
                  <ToggleSwitch
                    checked={considerThirdParty}
                    onChange={checked => {
                      setConsiderThirdParty(checked);
                      if (!checked) {
                        setSelectedThirdParty(null);
                      }
                    }}
                    onColor={grape}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    className="ml-2"
                  />
                </Col>
              </Row>
              {considerThirdParty ? (
                <Form.Group>
                  <Form.Label>Third Party</Form.Label>
                  <ThirdPartyDropdown
                    value={selectedThirdParty}
                    onChange={e => setSelectedThirdParty(e)}
                    skip={!considerThirdParty}
                  />
                </Form.Group>
              ) : (
                <Form.Group>
                  <Form.Label>Select Distributors</Form.Label>
                  <small className="text-muted d-block">
                    Choose which distributors to include in this multi-vendor stream.
                  </small>
                  <SearchableScrollableMultiSelect
                    initOptions={initOptions}
                    onChange={selectedOptions => {
                      const vendorIds = selectedOptions
                        ? selectedOptions.map(option => option.value)
                        : [];
                      setNewDataStreamSyncJob({
                        ...newDataStreamSyncJob,
                        selectedVendorIds: vendorIds,
                      });
                    }}
                    fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
                    optionMapper={vendor => ({
                      label: vendor.name,
                      value: vendor.id,
                    })}
                  />
                </Form.Group>
              )}
            </>
          )}
          <Row className="mb-2">
            <Col>
              <Form.Label className="mt-1">Process The Delta File</Form.Label>
            </Col>
            <Col className="d-flex align-items-center justify-content-end">
              <ToggleSwitch
                checked={newDataStreamSyncJob.processDeltaFile}
                onColor={grape}
                checkedIcon={false}
                uncheckedIcon={false}
                onChange={() =>
                  setNewDataStreamSyncJob({
                    ...newDataStreamSyncJob,
                    processDeltaFile: !newDataStreamSyncJob.processDeltaFile,
                  })
                }
                className="ml-2"
              />
            </Col>
          </Row>
          {/* Show sync_temp_skus_only toggle only for vendor streams */}
          {dataStream?.entity_type === DATA_STREAM_ENTITY.VENDOR && (
            <Row className="mb-3">
              <Col>
                <Form.Label className="mt-1">Sync temp SKUs only</Form.Label>
                <br />
                <small className="text-muted">
                  When enabled, only products with SKUs starting with VND will be synced.
                </small>
              </Col>
              <Col className="d-flex align-items-center justify-content-end">
                <ToggleSwitch
                  checked={newDataStreamSyncJob.syncTempSkusOnly}
                  onColor={grape}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onChange={() =>
                    setNewDataStreamSyncJob({
                      ...newDataStreamSyncJob,
                      syncTempSkusOnly: !newDataStreamSyncJob.syncTempSkusOnly,
                    })
                  }
                  className="ml-2"
                />
              </Col>
            </Row>
          )}
          {/* Lookback Period Configuration */}
          <Form.Group className="mt-3">
            <Form.Label>Lookback Attribute</Form.Label>
            <small className="text-muted d-block mb-2">
              Select an attribute to filter products based on modification date.
            </small>
            <Select
              isClearable
              isLoading={loadingDataStream}
              isDisabled={loadingDataStream}
              options={attributeOptions}
              value={attributeOptions.find(
                op => op.value === newDataStreamSyncJob.lookbackAttribute,
              )}
              onChange={op =>
                setNewDataStreamSyncJob({
                  ...newDataStreamSyncJob,
                  lookbackAttribute: op?.value || null,
                })
              }
              placeholder={loadingDataStream ? 'Loading attributes...' : 'Select attribute...'}
            />
          </Form.Group>
          <Button
            block
            disabled={loading}
            onClick={() => {
              const filters = {
                content_verification_status_filter:
                  newDataStreamSyncJob.contentVerificationStatus || null,
                product_status_filter: newDataStreamSyncJob.productStatus || null,
                sync_temp_skus_only_filter: newDataStreamSyncJob.syncTempSkusOnly || false,
                lookback_attribute: newDataStreamSyncJob.lookbackAttribute || null,
              };

              const basePayload = {
                destination_location_url: newDataStreamSyncJob.destinationLocationUrl,
                cron_expression: newDataStreamSyncJob.cronExpression,
                process_delta: newDataStreamSyncJob.processDeltaFile,
                filters: filters,
                file_name: newDataStreamSyncJob.fileName,
              };

              //vendor IDs and third party for data stream update (only for multi-vendor streams)
              if (dataStream?.entity_type === DATA_STREAM_ENTITY.MULTI_VENDOR) {
                if (considerThirdParty) {
                  basePayload.third_party = selectedThirdParty?.value || null;
                } else {
                  basePayload.selected_vendor_ids = newDataStreamSyncJob.selectedVendorIds;
                  basePayload.third_party = null;
                }
              }

              if (dataStreamSyncJob) {
                updateStreamSyncJob({
                  data_stream_sync_job_id: dataStreamSyncJob.id,
                  ...basePayload,
                });
              } else {
                createNewDataStreamSyncJob({
                  data_stream_id: dataStreamId,
                  ...basePayload,
                });
              }
            }}
          >
            Submit
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
}

CreateUpdateDataStreamSyncJob.propTypes = {
  onHide: PropTypes.func,
  dataStreamId: PropTypes.number,
  updateDataStreamSyncJobs: PropTypes.func,
  dataStreamSyncJob: PropTypes.object,
  dataStream: PropTypes.object,
};

export default CreateUpdateDataStreamSyncJob;
